import { supabase } from "./supabase";

// IR Proposta — leitura e escrita.
//
// Mesma disciplina do resto do hub: a tela LÊ views (`vw_irp_minhas_propostas`,
// `irp_questionario`) e ESCREVE por RPC `security definer`. `authenticated` não
// tem insert nem update em nenhuma tabela do módulo, e o teste 38 prova as três
// tentativas.
//
// O que este arquivo NÃO faz, de propósito:
//
//   * não decide se o aluno tem a ferramenta — quem decide é `vw_minha_vitrine`,
//     e a tela do curso já não mostra o card de quem não tem;
//   * não monta o questionário. Os enunciados vêm de `irp_questionario()`, com o
//     ano da temporada já interpolado. Enunciado dentro de `.tsx` foi o defeito do
//     Bubble: recuperá-los custou uma sessão de Chrome headless contra a página
//     pública, marcando todo "Sim" para revelar os condicionais;
//   * não escolhe o contrato nem calcula honorários — o primeiro é
//     `irp_dados_do_contrato`, e a conta do segundo é `lib/calculadoraIrpf.ts`,
//     módulo puro com prova própria. Aqui só entram a leitura dos parâmetros, a
//     leitura do mapa pergunta→campo e a gravação do resultado.

// ---------------------------------------------------------------------------
// Os oito status do kanban
// ---------------------------------------------------------------------------
// Vieram do dado real do Bubble, medidos em 19/08/2026 sobre 4.309 registros. A
// ORDEM é a do funil, e não a da contagem: é ela que faz o kanban ser lido da
// esquerda para a direita como o trabalho acontece. "Malha" e "Proposta recusada"
// ficam no fim porque são saídas, não etapas.
export const STATUS_IRP = [
  { chave: "em_negociacao", rotulo: "Em negociação" },
  { chave: "contrato", rotulo: "Contrato" },
  { chave: "documentacao", rotulo: "Documentação" },
  { chave: "dirpf_em_elaboracao", rotulo: "DIRPF em elaboração" },
  { chave: "entregue", rotulo: "Entregue" },
  { chave: "processada", rotulo: "Processada" },
  { chave: "malha", rotulo: "Malha" },
  { chave: "recusada", rotulo: "Proposta recusada" },
] as const;

export type StatusIrp = (typeof STATUS_IRP)[number]["chave"];

export function rotuloDoStatus(chave: string): string {
  return STATUS_IRP.find((s) => s.chave === chave)?.rotulo ?? chave;
}

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
/** Uma resposta do questionário, como a view agrega. */
export type RespostaIrp = {
  sim?: boolean;
  escolha?: string;
  valor_numero?: number;
  valor_texto?: string;
};

export type Proposta = {
  id: string;
  ano_calendario: number;
  nome: string;
  email: string | null;
  telefone: string | null;
  cpf: string | null;
  cep: string | null;
  endereco: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  status: StatusIrp;
  visualizada: boolean;
  origem: "formulario" | "manual";
  honorarios: number | null;
  /**
   * COMO se chegou no número: classificação, base, cada adicional, o ajuste de
   * posicionamento e a faixa. Nulo quando o valor foi digitado à mão — e o banco
   * é quem apaga isso (000071), porque detalhe descrevendo outro preço é pior que
   * detalhe nenhum.
   */
  honorarios_detalhe: unknown;
  contrato_gerado_em: string | null;
  criado_em: string;
  /** O banco já responde "dá para gerar o contrato?" — a tela não recalcula. */
  dados_completos: boolean;
  respostas: Record<string, RespostaIrp>;
};

export type Pergunta = {
  codigo: string;
  ordem: number;
  rotulo: string;
  tipo: "sim_nao" | "sim_nao_na" | "texto_longo" | "escolha";
  pai: string | null;
  valor_tipo: "numero" | "texto" | null;
  valor_rotulo: string | null;
  /**
   * Qual máscara o campo de valor usa (000069). `moeda` para os cinco campos em
   * reais, `inteiro` para a contagem de dependentes.
   *
   * Existe porque antes a tela decidia por SLUG (`q.codigo === "p1"`) — regra de
   * catálogo escrita em JavaScript, o mesmo defeito que a 000065 tirou dos
   * enunciados. Pergunta nova com campo em reais não precisa de deploy do front.
   */
  valor_formato: "inteiro" | "moeda" | null;
  opcoes: string[] | null;
};

export type Escrita<T> = { ok: true; res: T } | { ok: false; mensagem: string };

/**
 * A mensagem da RPC é escrita para o aluno ler. O fallback é para falha de rede,
 * onde o texto do driver não serve a ninguém. Mesmo desenho de `perfil.ts`.
 */
function falha(mensagem: string | undefined): { ok: false; mensagem: string } {
  return {
    ok: false,
    mensagem: mensagem?.trim()
      ? mensagem
      : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------
export async function carregarPropostas(): Promise<Proposta[]> {
  const { data, error } = await supabase
    .from("vw_irp_minhas_propostas")
    .select("*")
    // Desempate por `id` porque `criado_em` empata: a carga de uma temporada
    // inteira pode nascer no mesmo segundo, e ordenação sem desempate estável dá
    // duas respostas para o mesmo estado — a cicatriz da 000055.
    .order("criado_em", { ascending: false })
    .order("id", { ascending: false });
  if (error || !data) return [];
  return data as Proposta[];
}

export async function carregarQuestionario(): Promise<Pergunta[]> {
  const { data, error } = await supabase.rpc("irp_questionario");
  if (error || !data) return [];
  return data as Pergunta[];
}

/**
 * O token do formulário do aluno, criando na primeira vez.
 *
 * `rotacionar` troca o token: o link antigo para de receber e o cliente que já
 * tinha recebido cai na tela de "link não está ativo". É a razão de o token ser
 * próprio em vez do id do aluno, como faz o Bubble.
 */
export async function meuLink(rotacionar = false): Promise<Escrita<{ token: string }>> {
  const { data, error } = await supabase.rpc("meu_link_irp", { p_rotacionar: rotacionar });
  if (error) return falha(error.message);
  const linha = Array.isArray(data) ? data[0] : data;
  if (!linha?.token) return falha(undefined);
  return { ok: true, res: { token: linha.token as string } };
}

/** O endereço que o aluno copia e manda ao cliente. */
export function urlDoFormulario(token: string): string {
  return `${window.location.origin}/orcamento/${token}`;
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------
export async function moverProposta(id: string, status: StatusIrp): Promise<Escrita<Proposta>> {
  const { data, error } = await supabase.rpc("irp_mover_proposta", {
    p_proposta: id,
    p_status: status,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Proposta };
}

export async function marcarVista(id: string): Promise<Escrita<Proposta>> {
  const { data, error } = await supabase.rpc("irp_marcar_vista", { p_proposta: id });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Proposta };
}

export async function criarProposta(campos: {
  nome: string;
  email?: string;
  telefone?: string;
}): Promise<Escrita<Proposta>> {
  const { data, error } = await supabase.rpc("irp_criar_proposta", {
    p_nome: campos.nome,
    p_email: campos.email ?? null,
    p_telefone: campos.telefone ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Proposta };
}

/**
 * O aluno corrige os dados do CLIENTE e fecha os honorários.
 *
 * `null` = não mexe; string vazia = apaga. A convenção é a da 000042, e é ela que
 * permite este formulário mandar só o que mudou. As RESPOSTAS do questionário não
 * entram aqui e não têm por onde entrar: o que o cliente respondeu é fato, e a
 * única versão dele.
 */
export async function atualizarCliente(
  id: string,
  campos: Partial<{
    nome: string;
    email: string;
    telefone: string;
    cpf: string;
    cep: string;
    endereco: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
    honorarios: number | null;
  }>,
): Promise<Escrita<Proposta>> {
  const { data, error } = await supabase.rpc("irp_atualizar_cliente", {
    p_proposta: id,
    p_nome: campos.nome ?? null,
    p_email: campos.email ?? null,
    p_telefone: campos.telefone ?? null,
    p_cpf: campos.cpf ?? null,
    p_cep: campos.cep ?? null,
    p_endereco: campos.endereco ?? null,
    p_complemento: campos.complemento ?? null,
    p_bairro: campos.bairro ?? null,
    p_cidade: campos.cidade ?? null,
    p_estado: campos.estado ?? null,
    p_honorarios: campos.honorarios ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Proposta };
}

/**
 * O valor que APAGA os honorários da proposta.
 *
 * A convenção da RPC é "nulo = não mexe", e o formulário de dados do cliente
 * manda os honorários em toda gravação. Sem este sentinela, esvaziar o campo
 * mandaria `null` — "não mexe" —, e o número ficaria preso: o aluno apagaria na
 * tela, salvaria, e o valor antigo voltaria. Quem apaga é o negativo, que é a
 * convenção do banco desde a 000065.
 */
export const APAGAR_HONORARIOS = -1;

// ---------------------------------------------------------------------------
// A calculadora — parâmetros, mapa e gravação
// ---------------------------------------------------------------------------
export type Parametros = {
  base_simples: number;
  base_intermediaria: number;
  base_complexa: number;
  ad_dependente: number;
  ad_fonte_renda: number;
  ad_aluguel: number;
  ad_ganho_capital: number;
  ad_bolsa: number;
  ad_cripto: number;
  ad_carne_leao: number;
  ad_atividade_rural: number;
  ad_exterior: number;
  ad_retificacao: number;
};

export type ParametrosLidos = Parametros & {
  /** As três bases > 0. Quem responde é o banco; a tela não recalcula. */
  preenchido: boolean;
  atualizado_em: string | null;
};

/**
 * Os valores de referência do aluno.
 *
 * A view devolve UMA linha sempre — zerada para quem nunca preencheu —, então
 * aqui não há terceiro estado nem `null` de "não tem". O fallback existe só para
 * falha de rede, e vem zerado pelo mesmo motivo: valor semeado transformaria em
 * tabela nacional o que a 000071 existe para não semear.
 */
export async function carregarParametros(): Promise<ParametrosLidos> {
  const { data, error } = await supabase.from("vw_irp_meus_parametros").select("*").limit(1);
  if (error || !data || data.length === 0) {
    return {
      base_simples: 0, base_intermediaria: 0, base_complexa: 0,
      ad_dependente: 0, ad_fonte_renda: 0, ad_aluguel: 0, ad_ganho_capital: 0,
      ad_bolsa: 0, ad_cripto: 0, ad_carne_leao: 0, ad_atividade_rural: 0,
      ad_exterior: 0, ad_retificacao: 0,
      preenchido: false,
      atualizado_em: null,
    };
  }
  // Os treze campos passam por um lugar só. O `?? 0` é o que importa: a view
  // devolve a linha zerada por `coalesce`, mas um campo novo que ela ainda não
  // trouxesse chegaria `undefined`, e `undefined` numa SOMA vira `NaN` — que
  // atravessa a calculadora inteira sem estourar e aparece como "R$ NaN" no fim.
  const linha = data[0] as Record<string, unknown>;
  const num = (chave: string) => Number(linha[chave] ?? 0);
  return {
    base_simples: num("base_simples"),
    base_intermediaria: num("base_intermediaria"),
    base_complexa: num("base_complexa"),
    ad_dependente: num("ad_dependente"),
    ad_fonte_renda: num("ad_fonte_renda"),
    ad_aluguel: num("ad_aluguel"),
    ad_ganho_capital: num("ad_ganho_capital"),
    ad_bolsa: num("ad_bolsa"),
    ad_cripto: num("ad_cripto"),
    ad_carne_leao: num("ad_carne_leao"),
    ad_atividade_rural: num("ad_atividade_rural"),
    ad_exterior: num("ad_exterior"),
    ad_retificacao: num("ad_retificacao"),
    preenchido: linha.preenchido === true,
    atualizado_em: (linha.atualizado_em as string | null) ?? null,
  };
}

export async function salvarParametros(p: Parametros): Promise<Escrita<Parametros>> {
  const { data, error } = await supabase.rpc("atualizar_meus_parametros_irp", {
    p_base_simples: p.base_simples,
    p_base_intermediaria: p.base_intermediaria,
    p_base_complexa: p.base_complexa,
    p_ad_dependente: p.ad_dependente,
    p_ad_fonte_renda: p.ad_fonte_renda,
    p_ad_aluguel: p.ad_aluguel,
    p_ad_ganho_capital: p.ad_ganho_capital,
    p_ad_bolsa: p.ad_bolsa,
    p_ad_cripto: p.ad_cripto,
    p_ad_carne_leao: p.ad_carne_leao,
    p_ad_atividade_rural: p.ad_atividade_rural,
    p_ad_exterior: p.ad_exterior,
    p_ad_retificacao: p.ad_retificacao,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Parametros };
}

/**
 * Como cada pergunta do formulário alimenta a calculadora.
 *
 * Vem de `irp_mapa_calculo()`, que é separada de `irp_questionario()` de
 * propósito: aquela é executável por `anon` (o formulário público desenha a
 * partir dela) e isto é método de trabalho do aluno.
 */
export async function carregarMapaCalculo(): Promise<MapaCalculoLido[]> {
  const { data, error } = await supabase.rpc("irp_mapa_calculo");
  if (error || !data) return [];
  return data as MapaCalculoLido[];
}

export type MapaCalculoLido = {
  codigo: string;
  calc_campo: string | null;
  calc_conta_fonte: boolean;
  calc_alerta: string | null;
  calc_alerta_quando: boolean | null;
};

/**
 * Grava o honorário calculado na proposta, com o detalhe de como se chegou nele.
 *
 * A RPC confere a ARITMÉTICA do detalhe antes de gravar (soma das linhas, base +
 * adicionais + ajuste = total, total = honorário) e recusa zero. A mensagem dela
 * é escrita para o aluno ler, e é ela que aparece na tela.
 */
export async function salvarCalculo(
  proposta: string,
  honorarios: number,
  detalhe: Record<string, unknown>,
): Promise<Escrita<Proposta>> {
  const { data, error } = await supabase.rpc("irp_salvar_calculo", {
    p_proposta: proposta,
    p_honorarios: honorarios,
    p_detalhe: detalhe,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as Proposta };
}

// ---------------------------------------------------------------------------
// Dados profissionais do aluno (o CONTRATADO dos documentos)
// ---------------------------------------------------------------------------
export type DadosProfissionais = {
  escritorio: string | null;
  cnpj: string | null;
  crc: string | null;
  email: string | null;
  whatsapp: string | null;
  cep: string | null;
  endereco: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
};

const VAZIO: DadosProfissionais = {
  escritorio: null, cnpj: null, crc: null, email: null, whatsapp: null,
  cep: null, endereco: null, complemento: null, bairro: null, cidade: null, estado: null,
};

export async function carregarDadosProfissionais(): Promise<DadosProfissionais> {
  const { data, error } = await supabase
    .from("perfis_profissionais")
    .select("escritorio, cnpj, crc, email, whatsapp, cep, endereco, complemento, bairro, cidade, estado")
    .limit(1);
  // Sem linha é o estado normal de quem nunca preencheu — não é erro, e devolver
  // nulo faria a tela precisar de um terceiro estado só para isso.
  if (error || !data || data.length === 0) return VAZIO;
  return data[0] as DadosProfissionais;
}

export async function salvarDadosProfissionais(
  campos: Partial<DadosProfissionais>,
): Promise<Escrita<DadosProfissionais>> {
  const { data, error } = await supabase.rpc("atualizar_meus_dados_profissionais", {
    p_escritorio: campos.escritorio ?? null,
    p_cnpj: campos.cnpj ?? null,
    p_crc: campos.crc ?? null,
    p_email: campos.email ?? null,
    p_whatsapp: campos.whatsapp ?? null,
    p_cep: campos.cep ?? null,
    p_endereco: campos.endereco ?? null,
    p_complemento: campos.complemento ?? null,
    p_bairro: campos.bairro ?? null,
    p_cidade: campos.cidade ?? null,
    p_estado: campos.estado ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as DadosProfissionais };
}

// ---------------------------------------------------------------------------
// O contrato
// ---------------------------------------------------------------------------
export type DadosDoContrato = {
  exercicio: number;
  ano_calendario: number;
  honorarios: number | null;
  contratante: Record<string, string | null>;
  contratado: Record<string, string | null>;
  /** O que ainda falta, nomeado pelo banco. A tela lista; não recalcula. */
  faltando: string[];
};

export async function dadosDoContrato(id: string): Promise<Escrita<DadosDoContrato>> {
  const { data, error } = await supabase.rpc("irp_dados_do_contrato", { p_proposta: id });
  if (error) return falha(error.message);
  return { ok: true, res: data as DadosDoContrato };
}

export async function marcarContratoGerado(id: string): Promise<void> {
  // Sem tratamento de erro de propósito: é registro, não pré-requisito. Falhar
  // aqui não pode impedir o aluno de ficar com o documento que já foi montado no
  // navegador dele.
  await supabase.rpc("irp_marcar_contrato_gerado", { p_proposta: id });
}

// ---------------------------------------------------------------------------
// Formatação — só o que a ferramenta usa
// ---------------------------------------------------------------------------
/**
 * O nome do cliente como a tela o mostra. O orçamento que veio do Bubble pode não ter nome (000368: o
 * hub o traz como está, e o aluno completa) — sem isto, o cartão do kanban viraria um botão sem texto
 * e sem área de clique. O dado continua vazio; só a apresentação diz que ele falta.
 */
export function nomeDoCliente(p: { nome: string }): string {
  return p.nome.trim() || "Cliente sem nome";
}

export function formatarReais(valor: number | null): string {
  if (valor === null || valor === undefined) return "—";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** "11965450791" → "(11) 96545-0791". Texto livre passa intacto. */
export function formatarTelefone(valor: string | null): string {
  if (!valor) return "—";
  const so = valor.replace(/\D/g, "");
  if (so.length === 11) return `(${so.slice(0, 2)}) ${so.slice(2, 7)}-${so.slice(7)}`;
  if (so.length === 10) return `(${so.slice(0, 2)}) ${so.slice(2, 6)}-${so.slice(6)}`;
  return valor;
}

export function formatarCpf(valor: string | null): string {
  if (!valor) return "—";
  const so = valor.replace(/\D/g, "");
  if (so.length !== 11) return valor;
  return `${so.slice(0, 3)}.${so.slice(3, 6)}.${so.slice(6, 9)}-${so.slice(9)}`;
}
